import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SunDim, Envelope, LockSimple, Eye, EyeSlash, CircleNotch, ArrowRight } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const HERO = "https://images.unsplash.com/photo-1660330589257-813305a4a383?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const Field = ({ icon: Icon, right, ...props }) => (
  <div className="relative">
    <Icon size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
    <input {...props} className="w-full h-12 pl-11 pr-11 rounded-xl border border-slate-200 bg-white text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-shadow" />
    {right}
  </div>
);

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      navigate("/");
    } catch (err) { setError(formatApiError(err)); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex bg-ink-950 lg:bg-background">
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
        <img src={HERO} alt="Rooftop solar installation" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/60 to-ink-950/10" />
        <div className="relative mt-auto p-14 text-white max-w-xl">
          <h2 className="text-4xl font-heading font-extrabold tracking-tight leading-tight">Every solar project, from file to switch-on — in one place.</h2>
          <p className="text-white/70 mt-3">Customer files, Discom work, inspections, dispatch and installation for the whole team.</p>
        </div>
      </div>

      <div className="flex-1 flex flex-col">
        <div className="lg:hidden relative h-56 overflow-hidden">
          <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink-950/30 to-ink-950" />
          <div className="relative h-full flex items-end px-6 pb-8 text-white">
            <div><div className="text-[11px] uppercase tracking-[0.2em] font-bold text-brand-300">Anuj Solar</div><div className="font-heading text-2xl font-extrabold mt-1">Welcome back</div></div>
          </div>
        </div>
        <div className="flex-1 flex items-start lg:items-center justify-center bg-background rounded-t-[28px] lg:rounded-none -mt-6 lg:mt-0 relative px-6 pt-8 pb-10">
          <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
            <div className="flex items-center gap-3">
              <span className="w-12 h-12 rounded-2xl bg-brand-500 text-white flex items-center justify-center shadow-lg shadow-brand-500/30"><SunDim size={28} weight="bold" /></span>
              <div>
                <h1 className="text-2xl font-heading font-extrabold tracking-tight text-slate-900">Anuj Solar</h1>
                <p className="text-[10px] uppercase tracking-[0.22em] text-brand-700 font-bold">Workflow ERP</p>
              </div>
            </div>
            <div>
              <h2 className="text-lg font-heading font-bold text-slate-900">Sign in to your account</h2>
              <p className="text-sm text-slate-500 mt-0.5">Use the email and password shared by your admin.</p>
            </div>
            <Field icon={Envelope} data-testid="login-email-input" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="Email address" aria-label="Email" />
            <Field icon={LockSimple} data-testid="login-password-input" type={show ? "text" : "password"} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Password" aria-label="Password"
              right={<button type="button" aria-label={show ? "Hide password" : "Show password"} data-testid="login-toggle-password" onClick={() => setShow((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700">{show ? <EyeSlash size={18} /> : <Eye size={18} />}</button>} />
            {error && <p data-testid="login-error" className="text-sm text-rose-600 bg-rose-50 rounded-lg px-3 py-2">{error}</p>}
            <button data-testid="login-submit-button" type="submit" disabled={loading}
              className="tap w-full h-12 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-semibold flex items-center justify-center gap-2 shadow-lg shadow-brand-600/25 active:scale-[0.98] transition-[background-color,transform] disabled:opacity-60">
              {loading ? <><CircleNotch size={18} className="animate-spin" /> Signing in…</> : <>Sign in <ArrowRight size={18} weight="bold" /></>}
            </button>
            <p className="text-[11px] text-center text-slate-400 pt-2">Secure access · Anuj Solar team only</p>
          </form>
        </div>
      </div>
    </div>
  );
}
